<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";

const props = defineProps({
  editor: { type: Object, required: true },
});

const isVisible = ref(false);
const hoveredCol = ref(null);
const hoveredRow = ref(null);
let menuIsOpen = false;
let hideTimer = null;

function getContainer() {
  return (
    props.editor.view.dom.closest(".relative") ||
    props.editor.view.dom.parentElement
  );
}

function onMouseMove(e) {
  if (menuIsOpen) return;
  if (!props.editor?.view?.dom) return;

  const td = e.target.closest?.("td, th");
  const table = e.target.closest?.("table");
  // Also check if mouse is on a table-ctrl-btn (the dots themselves)
  const isOnCtrl = e.target.closest?.(".table-ctrl-btn-wrap");

  if (!td && !table && !isOnCtrl) {
    scheduleHide();
    return;
  }

  // If on control button, just cancel hide
  if (isOnCtrl) {
    cancelHide();
    return;
  }

  if (!td || !table) return;

  cancelHide();

  const cRect = getContainer().getBoundingClientRect();
  const tRect = table.getBoundingClientRect();
  const cellRect = td.getBoundingClientRect();

  hoveredCol.value = {
    left: cellRect.left - cRect.left + cellRect.width / 2 - 10,
    top: tRect.top - cRect.top - 26,
  };

  const tr = td.closest("tr");
  if (tr) {
    const rowRect = tr.getBoundingClientRect();
    // Position flush to the left edge of the table (inside the table border)
    hoveredRow.value = {
      left: tRect.left - cRect.left - 2,
      top: rowRect.top - cRect.top + rowRect.height / 2 - 10,
    };
  }

  isVisible.value = true;
}

function scheduleHide() {
  cancelHide();
  hideTimer = setTimeout(() => {
    if (!menuIsOpen) {
      isVisible.value = false;
      hoveredCol.value = null;
      hoveredRow.value = null;
    }
  }, 400);
}

function cancelHide() {
  clearTimeout(hideTimer);
}

function onMouseLeave() {
  if (menuIsOpen) return;
  scheduleHide();
}

function onMenuToggle(open) {
  menuIsOpen = open;
  if (open) {
    cancelHide();
  } else {
    scheduleHide();
  }
}

function afterAction() {
  isVisible.value = false;
  hoveredCol.value = null;
  hoveredRow.value = null;
  menuIsOpen = false;
  // Re-check after DOM updates — if still in table, show controls again
  setTimeout(() => {
    menuIsOpen = false;
    onSelectionUpdate();
  }, 200);
}

function colAction(action) {
  try {
    props.editor.chain().focus()[action]().run();
  } catch {
    // ignore if table was removed
  }
  afterAction();
}

function rowAction(action) {
  try {
    props.editor.chain().focus()[action]().run();
  } catch {
    // ignore if table was removed
  }
  afterAction();
}

let editorDom = null;

function onSelectionUpdate() {
  if (menuIsOpen) return;
  if (!props.editor?.view?.dom) return;
  if (!props.editor.isActive("table")) return;

  try {
    const { view } = props.editor;
    const { from } = view.state.selection;
    const domInfo = view.domAtPos(from);
    const el =
      domInfo.node instanceof HTMLElement
        ? domInfo.node
        : domInfo.node?.parentElement;
    if (!el) return;

    const td = el.closest("td, th");
    const table = el.closest("table");
    if (!td || !table) return;

    cancelHide();
    const cRect = getContainer().getBoundingClientRect();
    const tRect = table.getBoundingClientRect();
    const cellRect = td.getBoundingClientRect();

    hoveredCol.value = {
      left: cellRect.left - cRect.left + cellRect.width / 2 - 10,
      top: tRect.top - cRect.top - 26,
    };

    const tr = td.closest("tr");
    if (tr) {
      const rowRect = tr.getBoundingClientRect();
      hoveredRow.value = {
        left: tRect.left - cRect.left - 2,
        top: rowRect.top - cRect.top + rowRect.height / 2 - 10,
      };
    }

    isVisible.value = true;
  } catch {
    // ignore
  }
}

function attachListeners() {
  if (editorDom) return;
  try {
    editorDom = props.editor?.view?.dom;
  } catch {
    editorDom = null;
  }
  if (!editorDom) return;
  editorDom.addEventListener("mousemove", onMouseMove);
  editorDom.addEventListener("mouseleave", onMouseLeave);
  props.editor.on("selectionUpdate", onSelectionUpdate);
}

function detachListeners() {
  if (!editorDom) return;
  editorDom.removeEventListener("mousemove", onMouseMove);
  editorDom.removeEventListener("mouseleave", onMouseLeave);
  props.editor.off("selectionUpdate", onSelectionUpdate);
}

onMounted(() => {
  // Editor may not be ready yet — try now, retry on next ticks
  attachListeners();
  if (!editorDom) {
    const retryInterval = setInterval(() => {
      attachListeners();
      if (editorDom) clearInterval(retryInterval);
    }, 100);
    // Stop trying after 5s
    setTimeout(() => clearInterval(retryInterval), 5000);
  }
});

onBeforeUnmount(() => {
  detachListeners();
  clearTimeout(hideTimer);
});
</script>

<template>
  <div
    v-if="isVisible"
    class="table-controls"
    @mouseenter="cancelHide"
    @mouseleave="scheduleHide"
  >
    <!-- Column button — above hovered column -->
    <div
      v-if="hoveredCol"
      class="table-ctrl-btn-wrap"
      :style="{ top: hoveredCol.top + 'px', left: hoveredCol.left + 'px' }"
    >
      <DropdownMenu @update:open="onMenuToggle">
        <DropdownMenuTrigger as-child>
          <button class="table-ctrl-btn">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" :side-offset="4">
          <DropdownMenuItem @click="colAction('addColumnBefore')">
            Столбец слева
          </DropdownMenuItem>
          <DropdownMenuItem @click="colAction('addColumnAfter')">
            Столбец справа
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="colAction('toggleHeaderColumn')">
            Заголовок столбца
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            @click="colAction('deleteColumn')"
            class="text-destructive"
          >
            Удалить столбец
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>

    <!-- Row button — left edge of table, overlapping border -->
    <div
      v-if="hoveredRow"
      class="table-ctrl-btn-wrap"
      :style="{ top: hoveredRow.top + 'px', left: hoveredRow.left + 'px' }"
    >
      <DropdownMenu @update:open="onMenuToggle">
        <DropdownMenuTrigger as-child>
          <button class="table-ctrl-btn table-ctrl-btn--row">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="5" cy="12" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="19" cy="12" r="2" />
            </svg>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="left" :side-offset="4">
          <DropdownMenuItem @click="rowAction('addRowBefore')">
            Строка сверху
          </DropdownMenuItem>
          <DropdownMenuItem @click="rowAction('addRowAfter')">
            Строка снизу
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="rowAction('toggleHeaderRow')">
            Заголовок строки
          </DropdownMenuItem>
          <DropdownMenuItem @click="rowAction('mergeCells')">
            Объединить ячейки
          </DropdownMenuItem>
          <DropdownMenuItem @click="rowAction('splitCell')">
            Разделить ячейку
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            @click="rowAction('deleteRow')"
            class="text-destructive"
          >
            Удалить строку
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            @click="rowAction('deleteTable')"
            class="text-destructive"
          >
            Удалить таблицу
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
</template>

<style scoped>
.table-controls {
  pointer-events: auto;
}

.table-ctrl-btn-wrap {
  position: absolute;
  z-index: 30;
}

.table-ctrl-btn {
  @apply w-5 h-5 flex items-center justify-center rounded;
  @apply text-muted-foreground bg-gray-200 border border-gray-300;
  @apply transition-all duration-150;
}

.table-ctrl-btn:hover {
  @apply bg-gray-300 text-foreground shadow-sm;
}

/* Row button sits on the left edge of the table, shifted left by its width */
.table-ctrl-btn--row {
  transform: translateX(-100%);
}
</style>
